import { useMemo } from "react";
import moment from "jalali-moment";
import { CroppingPatternModel } from "@agroyaar/sdk";
import ReportPage from "@/shared/components/report-template/presentation/pages/ReportPage";
import IntroDes from "@/features/reports/presentation/components/cropping-pattern/components/IntroDes";
import AveragesGdd from "@/features/reports/presentation/components/cropping-pattern/components/AveragesGdd";
import HumidityRisks from "@/features/reports/presentation/components/cropping-pattern/components/HumidityRisks";
import Introduction from "@/shared/components/report-template/presentation/components/introduction/Introduction";
import CompositeIndex from "@/features/reports/presentation/components/cropping-pattern/components/CompositeIndex";
import TemperatureRisks from "@/features/reports/presentation/components/cropping-pattern/components/TemperatureRisks";
import OptimalSuggestedArea from "@/features/reports/presentation/components/cropping-pattern/components/OptimalSuggestedArea";
import SoilAdaptabilityIndex from "@/features/reports/presentation/components/cropping-pattern/components/SoilAdaptabilityIndex";
import IrrigationRequirement from "@/features/reports/presentation/components/cropping-pattern/components/IrrigationRequirement";
import DailyTemperatureChange from "@/features/reports/presentation/components/cropping-pattern/components/DailyTemperatureChange";
import AnnuallyPerformancePotential from "@/features/reports/presentation/components/cropping-pattern/components/AnnuallyPerformancePotential";
import CroppingPatternDefinition from "@/features/reports/presentation/components/cropping-pattern/components/CroppingPatternDefinition";
import ClimaticAdaptabilityIndex from "@/features/reports/presentation/components/cropping-pattern/components/ClimaticAdaptabilityIndex";

interface CroppingPatternReportPageProps {
  isLoading: boolean;
  croppingPattern: CroppingPatternModel | undefined;
}

const CroppingPatternReportPage = ({
  isLoading,
  croppingPattern,
}: CroppingPatternReportPageProps) => {
  const temperatureRisksChartPerPage = 2;
  const temperatureRisksPage = useMemo(
    () =>
      croppingPattern?.reports &&
      Math.ceil(
        croppingPattern?.reports?.length / temperatureRisksChartPerPage
      ),
    [croppingPattern?.reports]
  );

  const chartYears = useMemo(
    () =>
      croppingPattern?.reports[0]?.annuallyNetIrrigationRequirement?.map(
        ({ year }) => moment(`${year}-04-01`).format("jYYYY")
      ),
    [croppingPattern?.reports]
  );

  return (
    <div>
      <ReportPage isLoading={isLoading}>
        {croppingPattern && (
          <>
            <Introduction
              serviceType="CROPPING_PATTERN"
              farmer={croppingPattern.farmer}
              farm={croppingPattern.farm}
              irrigationSource={croppingPattern.irrigationSource}
              introDes={
                <IntroDes
                  croppingPatternReports={croppingPattern}
                  createdAt={croppingPattern.createdAt}
                />
              }
            />
            <CroppingPatternDefinition />

            <CompositeIndex croppingPatternReports={croppingPattern.reports} />

            <OptimalSuggestedArea
              croppingPatternReports={croppingPattern.reports}
            />

            <AnnuallyPerformancePotential
              croppingPatternReports={croppingPattern.reports}
              chartYears={chartYears!}
            />

            <ClimaticAdaptabilityIndex
              croppingPatternReports={croppingPattern.reports}
            />

            <DailyTemperatureChange
              dailyMaximumTemperatureChange={
                croppingPattern.dailyMaximumTemperatureChange
              }
              dailyMeanTemperatureChange={
                croppingPattern.dailyMeanTemperatureChange
              }
              dailyMinimumTemperatureChange={
                croppingPattern.dailyMinimumTemperatureChange
              }
            />

            <AveragesGdd
              croppingPatternReports={croppingPattern.reports}
              chartYears={chartYears!}
            />

            <TemperatureRisks
              croppingPatternReports={croppingPattern.reports}
              temperatureRisksPage={temperatureRisksPage!}
              temperatureRisksChartPerPage={temperatureRisksChartPerPage}
            />

            <HumidityRisks
              croppingPatternReports={croppingPattern.reports}
              pageNumber={8 + temperatureRisksPage!}
            />

            <SoilAdaptabilityIndex
              croppingPatternReports={croppingPattern.reports}
              pageNumber={9 + temperatureRisksPage!}
            />

            <IrrigationRequirement
              croppingPatternReports={croppingPattern.reports}
              chartYears={chartYears!}
              pageNumber={10 + temperatureRisksPage!}
            />
          </>
        )}
      </ReportPage>
    </div>
  );
};

export default CroppingPatternReportPage;
